import type {
  SignInErrorResponse,
  TokenResponse,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  useRequestOauthToken,
  ApiContextProvider,
  useGetAccountWithAccessToken,
  useGetPendingCoreConsentsWithAccessToken,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { isAxiosError } from "axios"
import { unix } from "dayjs"
import {
  useMemo,
  useState,
  useEffect,
} from "react"

import { MissingInfoFlow } from "./MissingInfoFlow"
import { SignInPage } from "./SignInPage"
import { CountryProtectedPage } from "src/components"
import {
  FollowedUsersProvider,
  useCountrySelectionContext,
  useOverlayLoader,
  usePageLifecycleContext,
} from "src/contexts"
import {
  GenericErrorToast,
  SignInErrorToast,
} from "src/toasts"
import type { RoutePath } from "src/types"
import { PageLifecycle } from "src/types"
import {
  injectProps,
  noticeError,
} from "src/utils"

interface Props {
  p?: string;
  deviceType?: string;
  clientId: string;
  onSignIn?: (response: TokenResponse) => Promise<void>;
  goBack: () => void;
  forgotPasswordRoute: RoutePath;
  isOauth?: boolean;
}

const InvalidDatacenterErrorCode = 1028

export function ReturningUserFlow(props: Props) {
  const {
    p,
    deviceType,
    clientId,
    goBack,
    onSignIn,
    forgotPasswordRoute,
    isOauth = false,
  } = props

  const { country } = useCountrySelectionContext()
  const pageLifecycle = usePageLifecycleContext()
  const { showToast } = useToasterContext()
  const requestOauthToken = useRequestOauthToken()
  const getAccountWithAccessToken = useGetAccountWithAccessToken()
  const getPendingConsentsWithAccessToken =
    useGetPendingCoreConsentsWithAccessToken()

  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  // when authResponse is set we display the missing info flow
  const [
    authResponse,
    setAuthResponse,
  ] = useState<TokenResponse | null>(null)
  const [
    currentPassword,
    setCurrentPassword,
  ] = useState("")

  const signInCallback = useMemo(
    () => (!onSignIn || !requestOauthToken)
      ? undefined
      : async (email: string, password: string) => {
        try {
          if (!getPendingConsentsWithAccessToken) {
            throw new Error("getPendingConsentsWithAccesToken is not available")
          }

          if (!getAccountWithAccessToken) {
            throw new Error("getAccountWithAccessToken is not available")
          }

          setIsOverlayLoaderVisible(true)

          const response = await requestOauthToken({
            username: email,
            password,
            client_id: clientId,
            grant_type: "password",
            p,
            device_type: deviceType,
          })

          const [
            consentResponse,
            accountResponse,
          ] = await Promise.all([
            getPendingConsentsWithAccessToken(response.data.access_token),
            getAccountWithAccessToken(response.data.access_token),
          ])

          // launch the missing info flow if the user hasn't seen the latest consents or are missing personal information
          if (
            consentResponse.data.required ||
            consentResponse.data.optional ||
            !accountResponse.data?.account?.birthdate
          ) {
            setAuthResponse(response.data)
            setCurrentPassword(password)
          } else {
            await onSignIn(response.data)
          }
        } catch (e) {
          noticeError(
            e,
            "SignIn",
            { email },
          )

          let error: string | undefined = undefined

          if (isAxiosError(e) && e.response) {
            error = (e.response.data as SignInErrorResponse).error
          }

          const code = error?.startsWith(`${InvalidDatacenterErrorCode}`) ? InvalidDatacenterErrorCode : undefined

          const ErrorToast = injectProps(
            SignInErrorToast,
            { code },
          )

          showToast(
            "sign-in-error-toast",
            ErrorToast,
          )

          if (code) {
            setTimeout(
              goBack,
              // delay the navigation for Oauth so the user has time to read
              // the message before they are redirect
              isOauth ? 5000 : 0,
            )
          }
        } finally {
          setIsOverlayLoaderVisible(false)
        }
      },
    [
      onSignIn,
      getPendingConsentsWithAccessToken,
      requestOauthToken,
      getAccountWithAccessToken,
      setIsOverlayLoaderVisible,
      clientId,
      p,
      deviceType,
      showToast,
      goBack,
      isOauth,
    ],
  )

  const onCompleteMissingInfo = useMemo(
    () => !authResponse
      ? null
      : async () => {
        try {
          if (!onSignIn) {
            throw new Error("missing sign in handler")
          }

          await onSignIn(authResponse)
        } catch (e) {
          noticeError(
            e,
            "SignIn",
          )

          showToast(
            "sign-in-error-toast",
            GenericErrorToast,
          )
        }
      },
    [
      onSignIn,
      authResponse,
      showToast,
    ],
  )

  // return the user to the sign in page when their access_token expires
  useEffect(
    () => {
      if (!authResponse) {
        return
      }

      const isVisible = (
        pageLifecycle === PageLifecycle.Active ||
        pageLifecycle === PageLifecycle.Passive
      )

      if (!isVisible) {
        return
      }

      const timeUntilExpiration = unix(authResponse.created_at)
        .add(
          authResponse.expires_in ?? 0,
          "second",
        )
        .diff(
          undefined,
          "ms",
        )

      const timeoutId = setTimeout(
        () => {
          setAuthResponse(null)
          setCurrentPassword("")
        },
        timeUntilExpiration,
      )

      return () => {
        clearTimeout(timeoutId)
      }
    },
    [
      authResponse,
      pageLifecycle,
    ],
  )

  if (onCompleteMissingInfo) {
    /*
     * this wraps the missing info flow in an ApiContext with an access token
     * to create a separate authenticated context for the user to update their
     * information before the are granted access to the rest of the application
     */
    return (
      <ApiContextProvider
        baseURL={country?.sugarmate_api_base_url}
        accessToken={authResponse?.access_token}
      >
        <FollowedUsersProvider>
          <MissingInfoFlow
            onComplete={onCompleteMissingInfo}
            currentPassword={currentPassword}
          />
        </FollowedUsersProvider>
      </ApiContextProvider>
    )
  }

  return (
    <CountryProtectedPage>
      <SignInPage
        goBack={goBack}
        forgotPasswordRoute={forgotPasswordRoute}
        signIn={signInCallback}
      />
    </CountryProtectedPage>
  )
}
